import L from "leaflet";
import { AnalisisLaboratorioResponse } from "../../../../modulos/fertilizacion/response/AnalisisLaboratorioResponse";
import { AnalisisRegistroResponse } from "../../../../modulos/fertilizacion/response/AnalisisRegistroResponse";
import { ClienteFincaLoteResponse } from "../../../../modulos/terceros/response/ClienteFincaLoteResponse";
import {
  ParametroBoton,
  parametrosConBoton,
} from "../../../../utils/fertilizacionUtils";
import { _2d } from "../../../../utils/numberUtils";
import {
  BaseMapRender,
  MapGroup,
  PopupConfig,
} from "../../../base/map/BaseMapRender";
import { ConfigTopActions } from "../../../implementations/map/TopActions";
import { FertilizacionColorModeControl } from "./FertilizacionColorModeControl";

/** Parámetro que se colorea por defecto al abrir un análisis (o el primero de
 *  la lista si no está presente). */
const PARAMETRO_DEFAULT = "ph";

/** Mapa del módulo de fertilización: colorea los lotes según el análisis
 *  seleccionado en el sidebar y el parámetro activo del panel superior (su
 *  `color` del semáforo). El panel superior no lleva selector de tooltip; monta
 *  los botones de parámetro (`FertilizacionColorModeControl`). Ortomosaico sí,
 *  conteo no. */
export class FertilizacionMapRender extends BaseMapRender<AnalisisRegistroResponse> {
  constructor() {
    super();
    this.setOnMapInitializeEnd(() => {
      this._mapReady = true;
      this._refresh();
    });
  }

  /** Colorea el mapa según el análisis seleccionado; rearma los botones con los
   *  parámetros presentes en ese análisis. */
  setAnalisis(analisis: AnalisisLaboratorioResponse): void {
    this._analisis = analisis;
    this._parametros = parametrosConBoton(analisis);
    // Mantiene el parámetro activo si sigue presente; si no, vuelve al default.
    if (!this._parametros.some((p) => p.key === this._mode)) {
      this._mode = this._defaultMode();
    }
    this._colorModeControl?.setParametros(this._parametros, this._mode);
    this._refresh();
  }

  /** Sin datos propios: el análisis llega por `setAnalisis`. */
  protected doInitData(): Promise<AnalisisRegistroResponse[]> {
    return Promise.resolve([]);
  }

  /** Registro del lote dentro del análisis seleccionado (o null). */
  protected doGetResponse(
    lote: ClienteFincaLoteResponse,
    _responseList: AnalisisRegistroResponse[],
  ): AnalisisRegistroResponse | null {
    return this._analisis?.registros.find((r) => r.loteId === lote.id) ?? null;
  }

  /** Libera el control de botones de parámetro. */
  protected doDispose(): void {
    this._colorModeControl?.dispose();
  }

  /** Rellena el lote con el `color` del parámetro activo; contorno por defecto
   *  si el lote no lo tiene. */
  protected getStyle(
    lote: ClienteFincaLoteResponse,
    registro: AnalisisRegistroResponse | null,
  ): L.PathOptions {
    const fill = registro ? this._fillColor(registro) : null;
    if (!fill) return super.getStyle(lote, registro);
    return {
      fillColor: fill,
      fillOpacity: 0.55,
      color: "rgb(224, 73, 13)",
      weight: 2.5,
      opacity: 1,
    };
  }

  /** Sin selector de tooltip; ortomosaico sí, conteo no. Los botones de
   *  parámetro se montan en `onMountEnd`. */
  protected getConfigTopActions(): ConfigTopActions<AnalisisRegistroResponse> {
    return {
      toolTipItems: [],
      withOrtomosaico: true,
      withConteo: false,
      onMountEnd: (element) => this._mountColorMode(element),
    };
  }

  /** Popup con los items default del lote; sin edición. */
  protected getPopupConfig(): PopupConfig<AnalisisRegistroResponse> | null {
    return {
      bindItems: (lote) => this.getDefaultPopupItems(lote),
      withEditLote: false,
    };
  }

  /** Análisis seleccionado (o null hasta que el sidebar seleccione uno). */
  private _analisis: AnalisisLaboratorioResponse | null = null;
  /** Parámetros con botón para el análisis actual. */
  private _parametros: ParametroBoton[] = [];
  /** Parámetro de coloreo activo (arranca en pH). */
  private _mode = PARAMETRO_DEFAULT;
  /** Si el mapa ya terminó de inicializar (`_groupList` listo). */
  private _mapReady = false;
  /** Control de los botones de parámetro. */
  private _colorModeControl: FertilizacionColorModeControl | null = null;

  /** Parámetro por defecto: pH si está presente, si no el primero de la lista. */
  private _defaultMode(): string {
    if (this._parametros.some((p) => p.key === PARAMETRO_DEFAULT)) {
      return PARAMETRO_DEFAULT;
    }
    return this._parametros[0]?.key ?? "";
  }

  /** Monta el control de botones en el panel superior; al cambiar de parámetro,
   *  repinta. */
  private _mountColorMode(element: HTMLElement): void {
    this._colorModeControl = new FertilizacionColorModeControl();
    this._colorModeControl.init(element, (mode) => {
      this._mode = mode;
      this._refresh();
    });
    this._colorModeControl.setParametros(this._parametros, this._mode);
  }

  /** Color de relleno del lote = `color` del parámetro activo (gris si
   *  `no_range`); null (contorno) si el lote no tiene ese parámetro o es
   *  sentinel. */
  private _fillColor(registro: AnalisisRegistroResponse): string | null {
    const parametro = registro.parametros.find((p) => p.nombre === this._mode);
    if (!parametro || parametro.isSentinel) return null;
    return parametro.color;
  }

  /** Re-pinta estilo y tooltip de cada lote según el análisis/parámetro
   *  actuales. No-op hasta que el mapa termine de inicializar. */
  private _refresh(): void {
    if (!this._mapReady) return;
    this._groupList.forEach((group) => {
      const registro = this.doGetResponse(group.lote, []);
      group.response = registro;
      group.layer.setStyle(this.getStyle(group.lote, registro));

      group.layer.unbindTooltip();
      group.layer
        .bindTooltip(this._tooltipContent(group), {
          permanent: true,
          direction: "center",
          className: "leaflet-lote-tooltip",
        })
        .openTooltip();
    });
  }

  /** Tooltip: nombre del lote + valor + unidad del parámetro activo (solo el
   *  nombre si el lote no tiene ese parámetro). */
  private _tooltipContent(group: MapGroup<AnalisisRegistroResponse>): string {
    const nombre = group.lote.nombreLote;
    const parametro = group.response?.parametros.find(
      (p) => p.nombre === this._mode,
    );
    if (!parametro || parametro.valor == null) return nombre;
    // Oculta placeholders sin unidad real (ej. "pH_unit", "—", vacío).
    const unidad =
      parametro.unidad &&
      parametro.unidad !== "—" &&
      !parametro.unidad.includes("_unit")
        ? ` ${parametro.unidad}`
        : "";
    return `<div class="lote-tooltip-stack"><span class="lote-tooltip-nombre">${nombre}</span><span style="font-size:var(--sx-text-sm);opacity:0.95">${_2d(parametro.valor)}${unidad}</span></div>`;
  }
}
